import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Col, Input, Row, Select, Space, Table, Typography, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText } from '../format';

const { Text } = Typography;

const LIST_FIELDS = ['assetAccounts', 'excludedAccounts'];

/**
 * Which accounts are investments and the category row each account goes to.
 * The rows are those of the uploaded form.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Function} props.onSaved - Called after saving
 * @returns {JSX.Element} The settings form
 */
export default function InvestmentsSettings({ companyId, onSaved }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ['client-reports-investments-config', companyId],
    queryFn: () => API.clientReports.getInvestmentConfig(companyId),
  });

  useEffect(() => {
    if (!data?.data) return;
    setForm({
      ...data.data,
      mapping: data.data.mapping.map((mp, i) => ({ ...mp, id: i })),
    });
    setDirty(false);
  }, [data]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const updateMapping = (id, patch) =>
    update({ mapping: form.mapping.map((mp) => (mp.id === id ? { ...mp, ...patch } : mp)) });

  const saveMutation = useMutation({
    mutationFn: () =>
      API.clientReports.saveInvestmentConfig(companyId, {
        assetAccounts: form.assetAccounts,
        excludedAccounts: form.excludedAccounts,
        mapping: form.mapping.map(({ account, partner, row }) => ({ account, partner, row })),
      }),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({
        queryKey: ['client-reports-investments-config', companyId],
      });
      message.success(t('investments.saved'));
      onSaved?.();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  if (!form) return null;

  const rowOptions = form.rows.map((r) => ({ value: r.key, label: r.label }));

  return (
    <div>
      <Row gutter={16}>
        {LIST_FIELDS.map((name) => (
          <Col xs={24} md={12} key={name}>
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t(`investments.fields.${name}`)}</Text>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                {t(`investments.hints.${name}`)}
              </div>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                open={false}
                suffixIcon={null}
                tokenSeparators={[',', ' ']}
                value={form[name] || []}
                onChange={(value) => update({ [name]: value })}
              />
            </div>
          </Col>
        ))}
      </Row>

      <Text strong>{t('investments.fields.mapping')}</Text>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
        {form.rows.length ? t('investments.hints.mapping') : t('investments.noTemplate')}
      </div>
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={form.mapping}
        style={{ marginBottom: 8 }}
        columns={[
          {
            title: t('investments.account'),
            dataIndex: 'account',
            key: 'account',
            width: 160,
            render: (account, mp) => (
              <Input
                value={account}
                onChange={(e) => updateMapping(mp.id, { account: e.target.value.trim() })}
              />
            ),
          },
          {
            title: t('investments.partner'),
            dataIndex: 'partner',
            key: 'partner',
            width: 160,
            render: (partner, mp) => (
              <Input
                value={partner}
                placeholder={t('investments.anyPartner')}
                onChange={(e) => updateMapping(mp.id, { partner: e.target.value.trim() })}
              />
            ),
          },
          {
            title: t('investments.row'),
            dataIndex: 'row',
            key: 'row',
            render: (row, mp) => (
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder={t('investments.pickRow')}
                options={rowOptions}
                value={row || undefined}
                onChange={(value) => updateMapping(mp.id, { row: value })}
              />
            ),
          },
          {
            key: 'remove',
            width: 48,
            render: (_, mp) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={t('investments.removeMapping')}
                onClick={() => update({ mapping: form.mapping.filter((x) => x.id !== mp.id) })}
              />
            ),
          },
        ]}
      />
      <Space>
        <Button
          icon={<PlusOutlined />}
          disabled={!form.rows.length}
          onClick={() =>
            update({
              mapping: [
                ...form.mapping,
                { id: Math.max(-1, ...form.mapping.map((mp) => mp.id)) + 1, account: '', partner: '', row: '' },
              ],
            })
          }
        >
          {t('investments.addMapping')}
        </Button>
        <Button
          type="primary"
          className="btn-primary"
          disabled={!dirty}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('investments.save')}
        </Button>
        {dirty && <Text type="warning">{t('fccs.unsaved')}</Text>}
      </Space>
    </div>
  );
}
